/**
 * Storage Abstraction Layer
 *
 * Permite cambiar el provider de storage sin modificar el código de negocio.
 * Actualmente soporta: Supabase
 * Futuro: DigitalOcean Spaces, AWS S3
 *
 * @example
 * import { getStorageAdapter } from '@/lib/storage'
 *
 * const storage = getStorageAdapter()
 * await storage.upload('documentos-clientes', 'path/file.pdf', file)
 */

// Re-exports
export * from './types'
export { SupabaseStorageAdapter, supabaseStorage } from './supabaseAdapter'

import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import type { StorageAdapter, StorageProvider } from './types'
import { SupabaseStorageAdapter, supabaseStorage } from './supabaseAdapter'

/**
 * Provider actualmente configurado
 * En producción esto podría venir de variables de entorno
 */
let currentProvider: StorageProvider = 'supabase'

/**
 * Cache de adapters instanciados
 */
const adapters: Partial<Record<StorageProvider, StorageAdapter>> = {
  supabase: supabaseStorage,
}

/**
 * Obtiene el adapter de storage configurado
 * @returns StorageAdapter para el provider actual
 */
export function getStorageAdapter(client?: SupabaseClient<Database>): StorageAdapter {
  // Provider Supabase: si recibimos un cliente autenticado, devolvemos un adapter
  // ligado a él (token fresco por request). Sin cliente, cae al singleton global
  // (token refrescado cada 50 min) por compatibilidad con callers sin client.
  if (currentProvider === 'supabase' && client) {
    return new SupabaseStorageAdapter(client)
  }

  const adapter = adapters[currentProvider]

  if (!adapter) {
    throw new Error(`Storage provider '${currentProvider}' no está configurado`)
  }

  return adapter
}

/**
 * Cambia el provider de storage
 * @param provider - Nuevo provider a usar
 *
 * @example
 * // Para migración futura a DO Spaces
 * setStorageProvider('digitalocean')
 */
export function setStorageProvider(provider: StorageProvider): void {
  if (!adapters[provider]) {
    throw new Error(`Storage provider '${provider}' no está implementado`)
  }

  currentProvider = provider
}

/**
 * Registra un nuevo adapter de storage
 * @param provider - Nombre del provider
 * @param adapter - Implementación del StorageAdapter
 *
 * @example
 * // Registrar adapter de DO Spaces
 * registerStorageAdapter('digitalocean', new DOSpacesAdapter(config))
 */
export function registerStorageAdapter(
  provider: StorageProvider,
  adapter: StorageAdapter
): void {
  adapters[provider] = adapter
}

/**
 * Obtiene el provider actualmente configurado
 * @returns Nombre del provider actual
 */
export function getCurrentProvider(): StorageProvider {
  return currentProvider
}

/**
 * Bucket por defecto para documentos de clientes
 */
export const BUCKET_DOCUMENTOS = 'documentos-clientes'
